import { useCallback, useMemo, useRef, useState } from 'react'

import { useInputAmountValidate } from '@/shared/hooks/useInputAmountValidate'
import { useWalletBalances } from '@/shared/hooks/useWalletBalances'
import { TypeArgument } from '@/shared/gen/_framework/reified'
import { PositionConfigInfo, round, SUI, USDC } from '@kunalabs-io/kai'
import { useTokenPrice } from 'utils/useTokenPrice'
import Decimal from 'decimal.js'
import { getFormattedUsdValue } from 'utils/usdFormatters'

export const usePositionBalances = (
  configInfo: PositionConfigInfo<string, string, TypeArgument>
) => {
  const touched = useRef(false)

  const { isLoading: isLoadingCoinXUsdPrice, price: coinXUsdPrice } = useTokenPrice(
    configInfo.X,
    USDC
  )
  const { isLoading: isLoadingCoinYUsdPrice, price: coinYUsdPrice } = useTokenPrice(
    configInfo.Y,
    USDC
  )

  const balances = useWalletBalances()
  const [xBalance, yBalance] = useMemo(() => {
    if (configInfo && balances.infos) {
      const x = balances.infos.get(configInfo.X.typeName)
      const y = balances.infos.get(configInfo.Y.typeName)
      return [x, y]
    }
    return [undefined, undefined]
  }, [balances.infos, configInfo])

  const [xRawInputStr, setXRawInputStr] = useState('')
  const [yRawInputStr, setYRawInputStr] = useState('')

  const [sanitizedXOnBlur, setSanitizedXOnBlur] = useState('')
  const [sanitizedYOnBlur, setSanitizedYOnBlur] = useState('')

  const { sanitizedInputValue: sanitizedXValue, amount: xCollateral } = useInputAmountValidate(
    xRawInputStr,
    xBalance?.decimals || 0
  )

  let xCollateralError = false
  if (xCollateral && xBalance && xCollateral.int > xBalance.int) {
    xCollateralError = true
  }

  const { sanitizedInputValue: sanitizedYValue, amount: yCollateral } = useInputAmountValidate(
    yRawInputStr,
    yBalance?.decimals || 0
  )

  let yCollateralError = false
  if (yCollateral && yBalance && yCollateral.int > yBalance.int) {
    yCollateralError = true
  }

  if (
    (xCollateral?.int == 0n && yCollateral?.int == 0n) ||
    (!sanitizedXValue && !sanitizedYValue) ||
    (xCollateral?.int == 0n && !sanitizedYValue) ||
    (yCollateral?.int == 0n && !sanitizedXValue)
  ) {
    xCollateralError = true
    yCollateralError = true
  }

  const xCollateralUSD = useMemo(() => {
    if (isLoadingCoinXUsdPrice || !coinXUsdPrice || !xCollateral) {
      return ''
    }

    return getFormattedUsdValue(xCollateral.toNumber() * coinXUsdPrice.human.toNumber())
  }, [coinXUsdPrice, isLoadingCoinXUsdPrice, xCollateral])

  const yCollateralUSD = useMemo(() => {
    if (isLoadingCoinYUsdPrice || !coinYUsdPrice || !yCollateral) {
      return ''
    }

    return getFormattedUsdValue(yCollateral.toNumber() * coinYUsdPrice.human.toNumber())
  }, [coinYUsdPrice, isLoadingCoinYUsdPrice, yCollateral])

  const handleSetXOnBlur = useCallback((value: string) => {
    setSanitizedXOnBlur(value)
  }, [])

  const handleSetYOnBlur = useCallback((value: string) => {
    setSanitizedYOnBlur(value)
  }, [])

  const handleXChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    touched.current = true
    setXRawInputStr(e.target.value)
  }, [])

  const handleYChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    touched.current = true
    setYRawInputStr(e.target.value)
  }, [])

  const handleMaxClickX = useCallback(() => {
    let value: string
    if (!xBalance) {
      value = '0'
    } else if (configInfo.X.typeName === SUI.typeName) {
      value = Decimal.max(xBalance.toDecimal().sub(1), 0).toString()
    } else {
      value = xBalance.toString()
    }

    setXRawInputStr(value)
    setSanitizedXOnBlur(value)
  }, [xBalance, configInfo.X.typeName])

  const handleReverseInputValues = useCallback(() => {
    setXRawInputStr(sanitizedYValue)
    setSanitizedXOnBlur(sanitizedYValue)
    setYRawInputStr(sanitizedXValue)
    setSanitizedYOnBlur(sanitizedXValue)
  }, [sanitizedXValue, sanitizedYValue])

  const handleHalfClickX = useCallback(() => {
    if (!xBalance) {
      return
    }

    const numValue = xBalance.toNumber()
    let value: number
    if (configInfo.X.typeName === SUI.typeName) {
      value = Math.max(numValue / 2 - 1, 0)
    } else {
      value = numValue / 2
    }

    setXRawInputStr(value.toString())
    setSanitizedXOnBlur(String(round(value, xBalance.decimals)))
  }, [xBalance, configInfo.X.typeName])

  const handleMaxClickY = useCallback(() => {
    let value: string
    if (!yBalance) {
      value = '0'
    } else if (configInfo.Y.typeName === SUI.typeName) {
      value = Decimal.max(yBalance.toDecimal().sub(1), 0).toString()
    } else {
      value = yBalance.toString()
    }

    setYRawInputStr(value)
    setSanitizedYOnBlur(value)
  }, [yBalance, configInfo.Y.typeName])

  const handleHalfClickY = useCallback(() => {
    if (!yBalance) {
      return
    }

    const numValue = yBalance.toNumber()
    let value: number
    if (configInfo.Y.typeName === SUI.typeName) {
      value = Math.max(numValue / 2 - 1, 0)
    } else {
      value = numValue / 2
    }

    setYRawInputStr(value.toString())
    setSanitizedYOnBlur(String(round(value, yBalance.decimals)))
  }, [yBalance, configInfo.Y.typeName])

  return {
    xInfo: xBalance,
    sanitizedXValue,
    xCollateral,
    xCollateralDollars: xCollateralUSD,
    xCollateralError: touched.current ? xCollateralError : false,
    handleXChange,
    handleMaxClickX,
    handleHalfClickX,
    yInfo: yBalance,
    sanitizedYValue,
    yCollateral,
    yCollateralDollars: yCollateralUSD,
    yCollateralError: touched.current ? yCollateralError : false,
    handleYChange,
    handleMaxClickY,
    handleHalfClickY,
    sanitizedXOnBlur,
    sanitizedYOnBlur,
    handleSetXOnBlur,
    handleSetYOnBlur,
    handleReverseInputValues,
  }
}
